<template>
  <div class="mt-5">
    <b-container fluid>
      <div class="card mb-3">
        <div class="card-body py-2 px-3">
          <div class="row align-items-end">
            <div class="col-md-3">
              <div class="form-group m-0 p-0">
                <label class="small mb-0">Status</label>
                <select
                  class="form-control form-control-sm"
                  v-model="filterStatus"
                  @change="loadItems"
                >
                  <option value="">All</option>
                  <option value="0">Active</option>
                  <option value="1">Deleted</option>
                </select>
              </div>
            </div>
            <div class="col-md-4">
              <b-form-group label="Search" label-size="sm" class="mb-0">
                <b-form-input
                  v-model="filter"
                  type="search"
                  size="sm"
                  placeholder="Search..."
                ></b-form-input>
              </b-form-group>
            </div>
            <div class="col-md-5 text-right align-self-end">
              <button class="btn btn-success btn-sm" @click="openCreateModal">
                <i class="fas fa-plus"></i> Add Special Payment
              </button>
            </div>
          </div>
        </div>
      </div>

      <b-table
        :busy="isBusy"
        :items="filteredItems"
        :fields="fields"
        :filter="filter"
        :current-page="currentPage"
        :per-page="perPage"
        stacked="md"
        show-empty
        small
        hover
        @filtered="onFiltered"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>

        <template #cell(amount)="{ value }">
          {{ formatCurrency(value) }}
        </template>

        <template #cell(deleted)="{ value }">
          <b-badge :variant="value == 0 ? 'success' : 'danger'">
            {{ value == 0 ? 'Active' : 'Deleted' }}
          </b-badge>
        </template>

        <template #cell(actions)="row">
          <b-button size="sm" variant="info" class="mr-1" @click="openEditModal(row.item)">
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button size="sm" variant="danger" @click="confirmDelete(row.item)">
            <i class="fas fa-trash"></i>
          </b-button>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            class="my-0"
          ></b-pagination>
        </b-col>
        <b-col md="6" class="my-1 text-right">
          <small class="text-muted">{{ totalRows }} record(s)</small>
        </b-col>
      </b-row>

      <!-- Create/Edit Modal -->
      <b-modal
        v-model="showModal"
        :title="editMode ? 'Edit Special Payment' : 'Add Special Payment'"
        size="lg"
        hide-footer
        centered
      >
        <form @submit.prevent="save">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Type <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="form.type"
                  placeholder="e.g. POST GRADUATE CONVOCATION"
                  required
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Amount <span class="text-danger">*</span></label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model="form.amount"
                  min="0"
                  required
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Service ID</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="form.service_id"
                  placeholder="Remita service ID"
                />
              </div>
            </div>
            <div class="col-md-6" v-if="editMode">
              <div class="form-group">
                <label>Status</label>
                <select class="form-control form-control-sm" v-model="form.deleted">
                  <option value="0">Active</option>
                  <option value="1">Deleted</option>
                </select>
              </div>
            </div>
          </div>
          <div class="text-right mt-3">
            <button type="button" class="btn btn-secondary mr-2" @click="showModal = false">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <b-spinner small v-if="saving"></b-spinner>
              {{ editMode ? "Update" : "Create" }}
            </button>
          </div>
        </form>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
export default {
  data() {
    return {
      isBusy: false,
      saving: false,
      items: [],
      filter: "",
      filterStatus: "",
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      showModal: false,
      editMode: false,
      editId: null,
      form: this.emptyForm(),
      fields: [
        { key: "id", label: "ID", sortable: true },
        { key: "type", label: "Type", sortable: true },
        { key: "amount", label: "Amount", sortable: true },
        { key: "service_id", label: "Service ID", sortable: true },
        { key: "deleted", label: "Status", sortable: true },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  computed: {
    filteredItems() {
      if (this.filterStatus === "") return this.items;
      return this.items.filter((i) => String(i.deleted) === this.filterStatus);
    },
  },
  watch: {
    filteredItems(val) {
      this.totalRows = val.length;
      this.currentPage = 1;
    },
  },
  created() {
    this.loadItems();
  },
  methods: {
    emptyForm() {
      return {
        type: "",
        amount: "",
        service_id: "",
        deleted: "0",
      };
    },
    formatCurrency(val) {
      return "₦" + Number(val || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 });
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    loadItems() {
      this.isBusy = true;
      axios
        .get("admin/special-payments")
        .then((response) => {
          this.items = response.data.data || [];
          this.totalRows = this.filteredItems.length;
        })
        .catch(() => {
          Toast.fire({ icon: "error", title: "Failed to load special payments" });
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    openCreateModal() {
      this.editMode = false;
      this.editId = null;
      this.form = this.emptyForm();
      this.showModal = true;
    },
    openEditModal(item) {
      this.editMode = true;
      this.editId = item.id;
      this.form = {
        type: item.type || "",
        amount: item.amount || "",
        service_id: item.service_id || "",
        deleted: String(item.deleted || 0),
      };
      this.showModal = true;
    },
    save() {
      this.saving = true;
      let request;
      if (this.editMode) {
        request = axios.put(`admin/special-payments/${this.editId}`, this.form);
      } else {
        request = axios.post("admin/special-payments", this.form);
      }

      request
        .then(() => {
          Toast.fire({
            icon: "success",
            title: this.editMode ? "Special payment updated" : "Special payment created",
          });
          this.showModal = false;
          this.loadItems();
        })
        .catch((err) => {
          const msg =
            err.response && err.response.data && err.response.data.error
              ? typeof err.response.data.error === "string"
                ? err.response.data.error
                : "Validation error"
              : "Operation failed";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    confirmDelete(item) {
      Swal.fire({
        title: "Delete this special payment?",
        text: `${item.type} — ₦${Number(item.amount).toLocaleString()}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteItem(item.id);
        }
      });
    },
    deleteItem(id) {
      axios
        .delete(`admin/special-payments/${id}`)
        .then(() => {
          Toast.fire({ icon: "success", title: "Special payment deleted" });
          this.loadItems();
        })
        .catch((err) => {
          const msg =
            err.response && err.response.data && err.response.data.error
              ? err.response.data.error
              : "Delete failed";
          Toast.fire({ icon: "error", title: msg });
        });
    },
  },
};
</script>
